import { clamp, mean } from './statistics'

export interface Prediction {
  prob: number
  outcome: 0 | 1
}

export function brierScore(predictions: readonly Prediction[]): number {
  if (predictions.length === 0) return 0
  let acc = 0
  for (const p of predictions) acc += (clamp(p.prob, 0, 1) - p.outcome) ** 2
  return acc / predictions.length
}

export interface CalibrationBucket {
  lower: number
  upper: number
  count: number
  avgPredicted: number
  observedFrequency: number
  gap: number
}

export function calibrationBuckets(
  predictions: readonly Prediction[],
  nBuckets = 10,
): CalibrationBucket[] {
  const buckets: CalibrationBucket[] = []
  for (let i = 0; i < nBuckets; i++) {
    const lower = i / nBuckets
    const upper = (i + 1) / nBuckets
    const inBucket = predictions.filter(
      (p) => p.prob >= lower && (i === nBuckets - 1 ? p.prob <= upper : p.prob < upper),
    )
    const avgPredicted = inBucket.length ? mean(inBucket.map((p) => p.prob)) : (lower + upper) / 2
    const observedFrequency = inBucket.length ? mean(inBucket.map((p) => p.outcome)) : 0
    buckets.push({
      lower,
      upper,
      count: inBucket.length,
      avgPredicted,
      observedFrequency,
      gap: inBucket.length ? observedFrequency - avgPredicted : 0,
    })
  }
  return buckets
}

export function expectedCalibrationError(predictions: readonly Prediction[], nBuckets = 10): number {
  if (predictions.length === 0) return 0
  const buckets = calibrationBuckets(predictions, nBuckets)
  let ece = 0
  for (const b of buckets) {
    if (b.count === 0) continue
    ece += (b.count / predictions.length) * Math.abs(b.gap)
  }
  return ece
}

export interface CalibrationReport {
  count: number
  brier: number
  ece: number
  score: number
  overconfidenceBias: number
  verdict: string
  buckets: CalibrationBucket[]
}

export function calibrationReport(predictions: readonly Prediction[]): CalibrationReport {
  const count = predictions.length
  const brier = brierScore(predictions)
  const ece = expectedCalibrationError(predictions)
  const score = clamp(100 * (1 - ece / 0.5), 0, 100)
  const overconfidenceBias = count
    ? mean(predictions.map((p) => p.prob)) - mean(predictions.map((p) => p.outcome))
    : 0

  let verdict: string
  if (count < 10) verdict = 'Not enough resolved predictions to judge calibration yet.'
  else if (overconfidenceBias > 0.07) verdict = 'Overconfident: your probabilities run hotter than reality.'
  else if (overconfidenceBias < -0.07) verdict = 'Underconfident: outcomes beat your stated probabilities.'
  else verdict = 'Reasonably calibrated. Keep logging to confirm.'

  return {
    count,
    brier,
    ece,
    score,
    overconfidenceBias,
    verdict,
    buckets: calibrationBuckets(predictions),
  }
}

export interface ClvResult {
  clvPct: number
  clvProb: number
  beatClose: boolean
}

export function closingLineValue(takenDecimal: number, closeDecimal: number): ClvResult {
  if (takenDecimal <= 1 || closeDecimal <= 1) {
    return { clvPct: 0, clvProb: 0, beatClose: false }
  }
  const clvPct = (takenDecimal / closeDecimal - 1) * 100
  const clvProb = 1 / closeDecimal - 1 / takenDecimal
  return { clvPct, clvProb, beatClose: takenDecimal > closeDecimal }
}
